import { useMutation } from '@tanstack/react-query';

import { queryClient } from '@/app/providers/react-query-provider';
import { StepForms } from '@/stores/slices/entity-slice';

import { apiEntityFetch, apiFetch } from '..';
import { ENTITY_VIEWS_QUERY_KEYS } from '../entity-views/queries';
import { UploadRequestResponse } from '../files/types';
import { USER_DASHBOARD_QUERY_KEYS } from '../user-dashboard/queries';
import { ENTITY_QUERY_KEYS } from './queries';
import { CheckRegistryBody, CheckRegistryResponse, EntityRole, EntityTeamLegalRole } from './types';

///////////////////////////////////////////////
type FileMetadata = {
  filename: string;
  contentType: string;
  file?: File;
};

export const _postFiles = ({ filename, contentType }: FileMetadata) => {
  return apiFetch('/files', {
    method: 'POST',
    body: { filename, contentType },
  });
};

///////////////////////////////////////////////

export type EntityUploadRequestPayload = {
  entityId: string;
  filename: string;
  contentType: string;
  contentEncoding?: string;
  category: 'logo' | 'profile' | 'document';
};

export const _postEntityUploadRequest = async ({
  entityId,
  filename,
  contentType,
  contentEncoding,
  category,
}: EntityUploadRequestPayload) => {
  if (!entityId) {
    throw new Error('entityId is required for entity file uploads');
  }

  return apiEntityFetch<UploadRequestResponse>(
    `/entities/${encodeURIComponent(String(entityId))}/upload-request`,
    {
      method: 'POST',
      body: {
        filename,
        contentType,
        contentEncoding,
        category,
      },
    },
  );
};

///////////////////////////////////////////////

export type EntityPayload = {
  legalName: string | null;
  displayName: string | null;
  state?: string | null;
  identificationNumber: string | null;
  country: string | null;
  registeredAt: string | null;
  type: string | null;
  logoImageId: string | null;
  entityEmail: string | null;
  entityWebsite: string | null;
  registeredAddressCity: string | null;
  registeredAddress: string | null;
  registeredAddressPostalCode: string | null;
};
export const _postEntities = (body: EntityPayload) => {
  return apiEntityFetch('/entities', {
    method: 'POST',
    body,
    keepalive: true,
  });
};

type PutEntityPayload = {
  id: string;
  body: Partial<EntityPayload & Pick<StepForms, 'shareStructure'>>;
};

export const _putEntities = ({ id, body }: PutEntityPayload) => {
  return apiEntityFetch<void>(`/entities/${id}`, {
    method: 'PUT',
    body,
    keepalive: true,
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities] });
    },
  });
};

export const useUpdateEntity = () => useMutation({ mutationFn: _putEntities });

///////////////////////////////////////////////

export type InvitationPayload = {
  email: string;
  legalRole: EntityTeamLegalRole;
  role: EntityRole;
  title: string;
  entityId: string;
};
type InvitationRes = {
  id: string;
};

export const _postInvitation = ({ entityId, ...body }: InvitationPayload) => {
  return apiEntityFetch<InvitationRes>(`/entities/${entityId}/invitations`, {
    method: 'POST',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
    },
  });
};

///////////////////////////////////////////////

type DeleteInvitationPayload = {
  entityId: string;
  invitationId: string;
};

export const _deleteInvitation = ({ entityId, invitationId }: DeleteInvitationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/invitations/${invitationId}`, {
    method: 'DELETE',
  });
};

///////////////////////////////////////////////

export type UpdateInvitationPayload = {
  entityId: string;
  invitationId: string;
  role: EntityRole;
  title: string;
  legalRole: EntityTeamLegalRole;
};
export const _updateInvitation = ({ entityId, invitationId, ...body }: UpdateInvitationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/invitations/${invitationId}`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
    },
  });
};

///////////////////////////////////////////////

type ApprovalInvitationPayload = {
  entityId: string;
  invitationId: string | null;
};

export const _approvalInvitation = ({ entityId, invitationId }: ApprovalInvitationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/invitations/${invitationId}/approval`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
      queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
  });
};

///////////////////////////////////////////////

type RejectionInvitationPayload = {
  entityId: string;
  invitationId: string | null;
};

export const _rejectionInvitation = ({ entityId, invitationId }: RejectionInvitationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/invitations/${invitationId}/rejection`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
      queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
  });
};
///////////////////////////////////////////////

export type DeleteMemberPayload = {
  entityId: string;
  memberId: string | null;
};

export const _deleteMember = ({ entityId, memberId }: DeleteMemberPayload) => {
  return apiEntityFetch(`/entities/${entityId}/members/${memberId}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
  });
};

///////////////////////////////////////////////

export type DeleteMemberOrInvitationPayload = {
  entityId: string;
  memberOrInvitationId: string | null;
};

export const _deleteMemberOrInvitation = ({
  entityId,
  memberOrInvitationId,
}: DeleteMemberOrInvitationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/directors/${memberOrInvitationId}`, {
    method: 'DELETE',
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
      queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
  });
};

///////////////////////////////////////////////

export type UpdateMemberPayload = {
  entityId: string;
  memberId: string;
  role: EntityRole;
  title: string;
  legalRole: EntityTeamLegalRole;
};

export const _updateMember = ({ entityId, memberId, ...body }: UpdateMemberPayload) => {
  return apiEntityFetch(`/entities/${entityId}/members/${memberId}`, {
    method: 'PUT',
    body,
    onResponse: () => {
      queryClient.invalidateQueries({
        queryKey: [ENTITY_VIEWS_QUERY_KEYS.teamAssociates, entityId],
      });
    },
  });
};

///////////////////////////////////////////////

export const _memberStatus = ({
  id,
  email,
  userId,
}: {
  id: string;
  email?: string;
  userId?: string;
}) => {
  const query = `${email ? `email=${encodeURIComponent(email)}` : ''}${userId ? `userId=${encodeURIComponent(userId)}` : ''}`;
  return apiEntityFetch(`/entities/${id}/team/member?${query}`, {
    method: 'GET',
  });
};

///////////////////////////////////////////////

export const _submitEntities = (id: string) => {
  return apiEntityFetch(`/entities/${id}/submit`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [USER_DASHBOARD_QUERY_KEYS.userProfile] });
    },
  });
};

///////////////////////////////////////////////

type PostVerificationsPayload = {
  id: string;
  fileId?: string;
  url?: string;
  type: string;
  format: 'url' | 'file';
  fileName?: string;
  fileSize?: number;
};

export const _postVerifications = ({ id, ...body }: PostVerificationsPayload) => {
  return apiEntityFetch(`/entities/${id}/verifications`, {
    method: 'POST',
    body,
  });
};

///////////////////////////////////////////////

type DeleteVerificationPayload = {
  entityId: string;
  verificationId: string;
};

export const _deleteVerification = ({ entityId, verificationId }: DeleteVerificationPayload) => {
  return apiEntityFetch(`/entities/${entityId}/verifications/${verificationId}`, {
    method: 'DELETE',
  });
};

///////////////////////////////////////////////
type ValidateEntitiesBody = {
  entityId?: string | null;
  fields: {
    legalName?: string | null;
    displayName?: string | null;
    country?: string | null;
    state?: string | null;
  };
  type: 'availability' | 'location';
};

export const _validateEntities = (body: ValidateEntitiesBody) => {
  return apiEntityFetch('/entities/validate', {
    method: 'POST',
    body,
  });
};

///////////////////////////////////////////////

export const _checkRegistry = (body: CheckRegistryBody) => {
  return apiEntityFetch<CheckRegistryResponse>('/entities/check-registry', {
    method: 'POST',
    body,
  });
};

///////////////////////////////////////////////

export const _approveEntity = (entityId: string) => {
  return apiEntityFetch(`/entities/${entityId}/approve`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities, entityId] });
    },
  });
};

export const _deleteEntity = (entityId: string) => {
  return apiEntityFetch(`/admin/entities/${entityId}`, {
    method: 'DELETE',
  });
};

export type BlockOrRejectEntityPayload = {
  entityId: string;
  reason: string;
  internalReference: string;
};

export const _blockOrRejectEntity = ({
  entityId,
  reason,
  internalReference,
}: BlockOrRejectEntityPayload) => {
  return apiEntityFetch(`/entities/${entityId}/block-or-reject`, {
    method: 'POST',
    body: { reason, internalReference },
  });
};

export const _unblockEntity = (entityId: string) => {
  return apiEntityFetch(`/entities/${entityId}/unblock`, {
    method: 'POST',
  });
};

type ResendInvitationParams = {
  email: string;
};

export const _resendEntityInvitation = (params: ResendInvitationParams) => {
  return apiEntityFetch(`/admin/entities/invitations/resend`, {
    method: 'POST',
    body: { email: params.email },
  });
};

export const _revokeEntityInvitation = (params: ResendInvitationParams) => {
  return apiEntityFetch(`/admin/entities/invitations/revoke`, {
    method: 'POST',
    body: { email: params.email },
  });
};

export const _resendEntityShareholderInvitation = (params: ResendInvitationParams) => {
  return apiEntityFetch(`/admin/entities/shareholder-invitations/resend`, {
    method: 'POST',
    body: { email: params.email },
  });
};

///////////////////////////////////////////////

export const _authorizeProvider = (entityId: string) => {
  return apiEntityFetch(`/admin/entities/${entityId}/authorize-provider`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities, entityId] });
    },
  });
};

export const _revokeProvider = (entityId: string) => {
  return apiEntityFetch(`/admin/entities/${entityId}/revoke-provider`, {
    method: 'POST',
    onResponse: () => {
      queryClient.invalidateQueries({ queryKey: [ENTITY_QUERY_KEYS.entities, entityId] });
    },
  });
};
